import { useEffect, useRef, useState } from 'react';
import { BlockRenderer } from '../components/AssistantBlockRenderer.tsx';
import { Builder } from '../components/builder/Builder.tsx';
import type { EditorMember } from '../components/builder/Editor.tsx';
import { serializeTeam } from '../components/builder/serialize.ts';
import { Composer } from '../components/Composer.tsx';
import { useChatStream } from '../hooks/useChatStream.ts';
import { ACTIVE_REGULATION, SUGGESTED_PROMPTS } from '../mock/data.ts';
import type { ConvTurn } from '../types.ts';

// Live chat surface — POSTs to /api/chat/stream, renders the agent's tool
// calls + cards live as they arrive. When the conversation is empty we show
// suggested prompts; clicking one fires the send.
//
// Session persistence: if the URL is /c/<id>, load that session's turns on
// mount. After first send the URL is rewritten to /c/<new_id> so refresh /
// share-link works.
export function LiveChat({ initialSessionId }: { initialSessionId?: string | null }) {
  const { turns, send, status, errorMessage, sessionId, loadSession } = useChatStream({
    regulationId: ACTIVE_REGULATION.id,
    initialSessionId,
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  // Draft team attached to the next outbound message. Set via the builder
  // modal's "attach to chat" button; cleared after send. Re-opening the
  // modal preserves it so the user can keep iterating.
  const [attachedTeam, setAttachedTeam] = useState<Array<EditorMember | null> | null>(null);

  // Load existing session on mount.
  useEffect(() => {
    if (initialSessionId) {
      void loadSession(initialSessionId);
    }
  }, [initialSessionId, loadSession]);

  // Auto-fire ?q=<prompt> from the home page hand-off, then strip the param
  // so a refresh doesn't re-send the same message. Guarded by sentRef so the
  // effect can't double-fire under StrictMode's mount/unmount/remount.
  const sentInitialRef = useRef(false);
  useEffect(() => {
    if (sentInitialRef.current) return;
    if (initialSessionId) return; // resumed session, don't pre-send
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q && q.trim()) {
      sentInitialRef.current = true;
      window.history.replaceState({}, '', '/chat');
      void send(q);
    }
  }, [initialSessionId, send]);

  // Sync URL when the session id changes (first send creates one server-side).
  useEffect(() => {
    if (!sessionId) return;
    const want = `/c/${sessionId}`;
    if (window.location.pathname !== want) {
      window.history.replaceState({}, '', want);
    }
  }, [sessionId]);

  // Auto-scroll when content changes.
  const lastBlockCount =
    turns.length > 0 && turns[turns.length - 1].role === 'assistant'
      ? (turns[turns.length - 1] as Extract<ConvTurn, { role: 'assistant' }>).blocks.length
      : 0;
  // biome-ignore lint/correctness/useExhaustiveDependencies: lastBlockCount is the meaningful trigger
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns.length, lastBlockCount]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 100px)',
      }}
    >
      <div
        ref={scrollRef}
        aria-live="polite"
        aria-busy={status === 'streaming'}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 18px',
        }}
      >
        <div
          style={{
            maxWidth: 920,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}
        >
          {turns.length === 0 ? (
            <EmptyPrompts onPick={(text) => send(text)} />
          ) : (
            turns.map((turn, i) => (
              <TurnView
                // biome-ignore lint/suspicious/noArrayIndexKey: conversation turns have no stable id; order is the id
                key={i}
                turn={turn}
                showThinking={
                  status === 'streaming' && i === turns.length - 1 && turn.role === 'assistant'
                }
              />
            ))
          )}
          {errorMessage && (
            <div
              className="mono"
              style={{
                padding: '8px 12px',
                background: 'rgba(201,119,102,0.10)',
                border: '1px solid rgba(201,119,102,0.40)',
                borderRadius: 4,
                color: 'var(--neg)',
                fontSize: 12,
              }}
            >
              {errorMessage}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '0 auto', width: '100%' }}>
        <Composer
          disabled={status === 'streaming'}
          onSubmit={(text) => {
            // If the user attached a team in the builder, prepend its
            // Showdown export so the agent picks it up via the existing
            // validate_team / paste path. Clear the attachment afterwards.
            if (attachedTeam) {
              const filled = attachedTeam.filter((m): m is EditorMember => m != null);
              if (filled.length > 0) {
                const exportText = serializeTeam(filled);
                const composed = text.trim().length > 0 ? `${exportText}\n${text}` : exportText;
                send(composed);
              } else {
                send(text);
              }
              setAttachedTeam(null);
            } else {
              send(text);
            }
          }}
          onOpenBuilder={() => setBuilderOpen(true)}
          attachedTeam={attachedTeam}
          onClearAttachment={() => setAttachedTeam(null)}
          onClickAttachment={() => setBuilderOpen(true)}
        />
      </div>
      {builderOpen && (
        <BuilderModal
          onClose={() => setBuilderOpen(false)}
          initialTeam={attachedTeam ?? undefined}
          onAttach={(team) => {
            setAttachedTeam(team);
            setBuilderOpen(false);
          }}
        />
      )}
    </div>
  );
}

function BuilderModal({
  onClose,
  initialTeam,
  onAttach,
}: {
  onClose: () => void;
  initialTeam?: Array<EditorMember | null>;
  onAttach?: (team: Array<EditorMember | null>) => void;
}) {
  // Escape closes; full-screen-ish (95vw × 92vh) so the three-column builder
  // doesn't get squashed. Click outside the panel closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="team builder"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-0)',
          border: '1px solid var(--line-1)',
          borderRadius: 6,
          width: 'min(1280px, 95vw)',
          height: 'min(900px, 92vh)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid var(--line-1)',
            background: 'var(--bg-1)',
          }}
        >
          <span
            className="caps mono"
            style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.1em' }}
          >
            team builder
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="close builder"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--fg-3)',
              fontSize: 18,
              cursor: 'pointer',
              padding: 0,
              width: 28,
              height: 28,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Builder regulationId="M-A" initialTeam={initialTeam} onAttach={onAttach} />
        </div>
      </div>
    </div>
  );
}

function TurnView({ turn, showThinking }: { turn: ConvTurn; showThinking?: boolean }) {
  if (turn.role === 'user') {
    return (
      <div className="fade-in" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          style={{
            maxWidth: '78%',
            padding: '8px 12px',
            background: 'var(--bg-2)',
            border: '1px solid var(--line-1)',
            borderRadius: 4,
            color: 'var(--fg-1)',
            fontSize: 13.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {turn.text}
        </div>
      </div>
    );
  }
  if (turn.blocks.length === 0) {
    return <TypingDots />;
  }
  // Collapse runs of consecutive `tool` blocks with the same tool name into a
  // single ToolCallGroup. Improves signal-to-noise when the agent dispatches
  // 6 parallel `get_pikalytics_stats` calls — the user sees one row with
  // "6 calls" instead of six near-identical lines.
  const grouped: Array<
    | { type: 'single'; block: ConvTurnAssistantBlock; index: number }
    | { type: 'group'; tool: string; blocks: ConvTurnAssistantBlock[]; index: number }
  > = [];
  for (let i = 0; i < turn.blocks.length; i++) {
    const b = turn.blocks[i];
    if (b.kind !== 'tool') {
      grouped.push({ type: 'single', block: b, index: i });
      continue;
    }
    const run: ConvTurnAssistantBlock[] = [b];
    while (i + 1 < turn.blocks.length) {
      const next = turn.blocks[i + 1];
      if (next.kind !== 'tool' || next.tool !== b.tool) break;
      run.push(next);
      i++;
    }
    if (run.length === 1) {
      grouped.push({ type: 'single', block: b, index: i });
    } else {
      grouped.push({ type: 'group', tool: b.tool, blocks: run, index: i });
    }
  }
  return (
    <div
      className="fade-in"
      style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: '90%' }}
    >
      {grouped.map((g) =>
        g.type === 'single' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: stream blocks have no stable id; order is the id
          <BlockRenderer key={g.index} block={g.block} />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: same
          <ToolCallGroup key={g.index} tool={g.tool} blocks={g.blocks} />
        ),
      )}
      {showThinking && <TypingDots />}
    </div>
  );
}

// Local alias — the union member shape from ConvTurn's `blocks` field.
type ConvTurnAssistantBlock = Extract<ConvTurn, { role: 'assistant' }>['blocks'][number];

function ToolCallGroup({
  tool,
  blocks,
}: {
  tool: string;
  blocks: ConvTurnAssistantBlock[];
}) {
  const [expanded, setExpanded] = useState(false);
  // Aggregate status: error wins, else running wins, else done.
  const statuses = blocks.map((b) => (b.kind === 'tool' ? b.status : 'done'));
  const aggregate: 'running' | 'done' | 'error' = statuses.includes('error')
    ? 'error'
    : statuses.includes('running')
      ? 'running'
      : 'done';
  const dotColor =
    aggregate === 'running' ? 'var(--accent-2)' : aggregate === 'done' ? 'var(--pos)' : 'var(--neg)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mono"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'var(--bg-2)',
          border: '1px solid var(--line-1)',
          borderLeftWidth: 2,
          borderLeftColor: dotColor,
          borderRadius: 3,
          fontSize: 11,
          color: 'var(--fg-2)',
          textAlign: 'left',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <span
          className={aggregate === 'running' ? 'dot pulse' : 'dot'}
          style={{ background: dotColor }}
          aria-hidden="true"
        />
        <span className="caps" style={{ color: 'var(--fg-1)', letterSpacing: '0.06em' }}>
          {tool}
        </span>
        <span style={{ color: 'var(--fg-3)' }}>· {blocks.length} calls</span>
        <span style={{ flex: 1 }} />
        <span className="caps" style={{ color: 'var(--fg-4)', fontSize: 9.5, letterSpacing: '0.08em' }}>
          {expanded ? '− collapse' : '+ expand'}
        </span>
      </button>
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 12 }}>
          {blocks.map((b, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: stream blocks have no stable id; order is the id
            <BlockRenderer key={i} block={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <div
      role="status"
      aria-label="agent is thinking"
      style={{ display: 'flex', gap: 4, padding: 4 }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="dot pulse"
          style={{
            background: 'var(--fg-3)',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  );
}

function EmptyPrompts({ onPick }: { onPick: (text: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 22,
          fontWeight: 400,
          color: 'var(--fg-1)',
          margin: 0,
        }}
      >
        What are we working on?
      </h2>
      <div className="prompt-grid">
        {SUGGESTED_PROMPTS.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              background: 'var(--bg-1)',
              border: '1px solid var(--line-1)',
              borderRadius: 4,
              fontSize: 13,
              color: 'var(--fg-2)',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              className="mono"
              style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.08em' }}
            >
              0{i + 1}
            </span>
            <span style={{ flex: 1 }}>{p}</span>
            <span style={{ color: 'var(--fg-4)' }}>→</span>
          </button>
        ))}
      </div>
    </div>
  );
}
